import Location from '@nmx/utils/dist/utilities/frontend/location';
import Session from '@nmx/utils/dist/utilities/frontend/session';
import { getSCVisitorId } from '@nmx/utils/dist/utilities/frontend/Analytics/dtm_helper';
import { isString, isPhoneNumber, isEmail, isZipCode, isEmpty } from '@nmx/utils/dist/utilities/validators';
import { sanitizePhone } from '@nmx/utils/dist/utilities/frontend/sanitize';

export const goals = [
  {
    name: 'Please select a goal...',
    value: '',
    disabled: true,
  },
  {
    name: 'buy a home',
    value: 'buy a home',
    disabled: false,
  },
  {
    name: 'save for college',
    value: 'save for college',
    disabled: false,
  },
  {
    name: 'save for retirement',
    value: 'save for retirement',
    disabled: false,
  },
  {
    name: 'protect my income',
    value: 'protect my income',
    disabled: false,
  },
  {
    name: 'plan for growing my family',
    value: 'plan for growing my family',
    disabled: false,
  },
  {
    name: 'protect my family',
    value: 'protect my family',
    disabled: false,
  },
  {
    name: 'grow my business',
    value: 'grow my business',
    disabled: false,
  },
  {
    name: 'grow my investments',
    value: 'grow my investments',
    disabled: false,
  },
  {
    name: "I'm not sure",
    value: "I'm not sure",
    disabled: false,
  },
];

export const incomes = [
  {
    name: 'Please select a range...',
    value: '',
    disabled: true,
  },
  {
    name: 'less than $40,000',
    value: 39999,
    disabled: false,
  },
  {
    name: '$40,001 - $75,000',
    value: 57500,
    disabled: false,
  },
  {
    name: '$75,001 - $100,000',
    value: 87500,
    disabled: false,
  },
  {
    name: '$100,001 - $150,000',
    value: 125000,
    disabled: false,
  },
  {
    name: '$150,001 - $200,000',
    value: 175000,
    disabled: false,
  },
  {
    name: '$200,001 - $300,000',
    value: 250000,
    disabled: false,
  },
  {
    name: 'more than $300,000',
    value: 300000,
    disabled: false,
  },
];

export const pronouns = [
  {
    name: 'Select one (optional)',
    value: '',
    disabled: false,
  },
  {
    name: 'He/Him/His',
    value: 'He/Him/His', // TODO: Remove the slashes in the values, once we start sending this to form-proxy
    disabled: false,
  },
  {
    name: 'She/Her/Hers',
    value: 'She/Her/Hers', // TODO: Remove the slashes in the values, once we start sending this to form-proxy
    disabled: false,
  },
  {
    name: 'They/Them/Theirs',
    value: 'They/Them/Theirs', // TODO: Remove the slashes in the values, once we start sending this to form-proxy
    disabled: false,
  },
];

// form flow analytics names that map to form field names for adobe shared calls on lead and service request forms
export const analyticsFlowNames = {
  policy_owner: {
    type: 'Are You Currently Working Together',
    value: 'Completed',
  },
  first_name: {
    type: 'First Name',
    value: 'Completed',
  },
  last_name: {
    type: 'Last Name',
    value: 'Completed',
  },
  pronoun: { type: 'gender-pronoun' },
  zip: {
    type: 'Zip',
    value: 'Completed',
  },
  phone: {
    type: 'Phone',
    value: 'Completed',
  },
  email: {
    type: 'Email',
    value: 'Completed',
  },
  comments: {
    type: 'Comments',
    value: 'Completed',
  },
  wealth_range: { type: 'Wealth Range' },
  income_range: { type: 'Income Range' },
  goal: { type: 'Goal' },
  age: {
    type: 'Age',
    value: 'Completed',
  },
};

export const getSourceAndTopic = (sourceParam, topicParam) => {
  let source = sourceParam;
  let topic = topicParam;

  if (Location.getQueryParam('intcmp') === 'fws-contact') {
    source = 'Field Website';
    topic = 'FR Contact Form';
  }

  return {
    source,
    topic,
  };
};

export const validatePolicyOwnerSelected = (policyOwner) => !!policyOwner && (policyOwner === 'Yes' || policyOwner === 'No');

export const validateFirstName = (firstName) => !isEmpty(firstName) && isString(firstName) && firstName.length <= 150;

export const validateLastName = (lastName) => !isEmpty(lastName) && isString(lastName) && lastName.length <= 150;

export const validateZipCode = (zip) => !isEmpty(zip) && isString(zip) && isZipCode(zip);

export const validatePhone = (phone) => !isEmpty(phone) && isPhoneNumber(phone);

export const validateEmail = (email) => !isEmpty(email) && isEmail(email);

export const validateAge = (age, policyOwner) => {
  if (policyOwner === 'Yes') {
    return true;
  }
  const ageString = (age || '').toString();
  const ageStringCheck = !!ageString && !isEmpty(ageString) && /^[0-9]+$/.test(ageString);
  const ageInt = parseInt(ageString, 10);

  return ageStringCheck && ageInt > 0 && ageInt < 125;
};

export const validateGoal = (goal, policyOwner) => {
  if (policyOwner === 'Yes') {
    return true;
  }

  return !!goal && !isEmpty(goal);
};

export const validateIncomeRange = (incomeRange, policyOwner) => {
  if (policyOwner === 'Yes') {
    return true;
  }

  return !!incomeRange && !isEmpty(incomeRange);
};

// only required for leads when income range is below $40K
export const validateWealthRange = (wealthRange, incomeRange, policyOwner) => {
  if (policyOwner === 'Yes' || (!!incomeRange && incomeRange > 39999)) {
    return true;
  }

  return !!wealthRange && !isEmpty(incomeRange);
};

export const validateComments = (comments, policyOwner) => {
  if (policyOwner === 'Yes') {
    // comment field required
    return !isEmpty(comments) && isString(comments) && comments.length <= 2500;
  }
  return true;
};

export const buildServiceRequestPayload = (values, agentnumber, recaptchaReponseToken, source, topic) => {
  const sourceAndTopic = getSourceAndTopic(source, topic);

  return {
    source: sourceAndTopic.source,
    topic: sourceAndTopic.topic,
    agent_number: agentnumber,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: sanitizePhone(values.phone),
    zip: values.zip,
    comments: values.comments,
    sc_visitor_id: getSCVisitorId(),
    recaptcha_response_token: recaptchaReponseToken,
  };
};

export const buildLeadPayload = (values, agentnumber, recaptchaReponseToken, source, topic) => {
  const sourceAndTopic = getSourceAndTopic(source, topic);
  let wealthRange;
  // do not pass wealthRange unless it is defined and was still a visible input field
  if (values.wealth_range && !!values.income_range && values.income_range <= 39999) {
    wealthRange = values.wealth_range;
  }

  return {
    campaign_name: values.campaign_name,
    age: values.age,
    agent_number: agentnumber,
    comments: `I am ${values.age} years old and my biggest financial goal is to ${values.goal}`,
    goal: values.goal,
    email: values.email,
    first_name: values.first_name,
    income_range: values.income_range,
    last_name: values.last_name,
    page_url: window
      ? window.location.href
      : '',
    phone: sanitizePhone(values.phone),
    recaptcha_response_token: recaptchaReponseToken,
    sc_visitor_id: getSCVisitorId(),
    source: sourceAndTopic.source,
    topic: sourceAndTopic.topic,
    utm_campaign: Session.get('utmCampaign'),
    utm_content: Session.get('utmContent'),
    utm_medium: Session.get('utmMedium'),
    utm_source: Session.get('utmSource'),
    utm_term: Session.get('utmTerm'),
    wealth_range: wealthRange,
    zip: values.zip,
  };
};
